import {
  collection,
  doc,
  FirestoreDataConverter,
  updateDoc,
} from 'firebase/firestore';
import { useCollectionData } from 'react-firebase-hooks/firestore';
import { firestore, auth } from '@/lib/firebase';
import { useMemo } from 'react';

export type Image = {
  id: string;
  name: string;
  url: string;
  uploadDate: Date;
  uploadedBy: string;
  isDeleted?: boolean;
};

const imageConverter: FirestoreDataConverter<Image> = {
  toFirestore: ({ id: _id, ...image }: Image) => image,
  fromFirestore: (snapshot) => {
    const data = snapshot.data();
    return {
      id: snapshot.id,
      name: data.name,
      url: data.url,
      uploadDate: data.uploadDate?.toDate(),
      uploadedBy: data.uploadedBy,
      isDeleted: data.isDeleted,
    };
  },
};

export function useImages() {
  const [images, loading, error] = useCollectionData<Image>(
    collection(firestore, 'images').withConverter(imageConverter),
    {
      snapshotListenOptions: { includeMetadataChanges: false },
    }
  );

  // Firestore queries can't match documents missing a field, so images
  // created before isDeleted existed are filtered out here instead.
  const visibleImages = useMemo(
    () => images?.filter((image) => !image.isDeleted),
    [images]
  );

  return [visibleImages, loading, error] as const;
}

export async function softDeleteImage(id: string) {
  await updateDoc(doc(firestore, 'images', id), { isDeleted: true });
}

export async function uploadImage({
  file,
  name,
}: {
  file: File;
  name: string;
}) {
  const user = auth.currentUser;

  if (!user) {
    throw new Error('User not authenticated');
  }

  const idToken = await user.getIdToken();
  const formData = new FormData();
  formData.append('file', file);
  formData.append('name', name);

  const response = await fetch(
    `${import.meta.env.VITE_MONITORING_DASHBOARD_API_URL}/images/upload`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
      body: formData,
    }
  );

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.statusText}`);
  }

  return response.json();
}
